import { useEffect, useState } from "react";
import api from "@/lib/api";
import { TIME_CATEGORIES } from "@/lib/constants";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

export default function TimeEntryModal({ open, setOpen, clients, defaultClientId, entry, onSaved }) {
  const [form, setForm] = useState({ client_id: "", date: "", start_time: "", end_time: "", category: "community", note: "" });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (entry) {
      setForm({
        client_id: entry.client_id, date: entry.date,
        start_time: (entry.start || "").slice(11, 16), end_time: (entry.end || "").slice(11, 16),
        category: entry.category || "autre", note: entry.note || "",
      });
    } else {
      setForm({
        client_id: defaultClientId || clients[0]?.id || "",
        date: new Date().toISOString().slice(0, 10),
        start_time: "", end_time: "", category: "community", note: "",
      });
    }
  }, [open, entry, defaultClientId, clients]);

  const submit = async () => {
    if (!form.client_id || !form.date || !form.start_time || !form.end_time) return;
    setLoading(true);
    try {
      if (entry) {
        await api.patch(`/time/entries/${entry.id}`, form);
        toast.success("Entrée modifiée");
      } else {
        await api.post("/time/entries", form);
        toast.success("Temps ajouté");
      }
      setOpen(false);
      window.dispatchEvent(new Event("sf:time"));
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur lors de l'enregistrement");
    } finally { setLoading(false); }
  };

  const sel = "mt-1.5 w-full text-sm border border-neutral-200 rounded-md px-2.5 py-2 bg-white";
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-md" data-testid="time-entry-modal" aria-describedby={undefined}>
        <DialogHeader><DialogTitle className="font-heading">{entry ? "Modifier le temps" : "Ajouter du temps"}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>Client</Label>
            <select data-testid="time-entry-client" value={form.client_id} onChange={set("client_id")} className={sel}>
              {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <div>
            <Label>Date</Label>
            <Input data-testid="time-entry-date" type="date" value={form.date} onChange={set("date")} className="mt-1.5" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Début</Label>
              <Input data-testid="time-entry-start" type="time" value={form.start_time} onChange={set("start_time")} className="mt-1.5" />
            </div>
            <div>
              <Label>Fin</Label>
              <Input data-testid="time-entry-end" type="time" value={form.end_time} onChange={set("end_time")} className="mt-1.5" />
            </div>
          </div>
          <div>
            <Label>Catégorie</Label>
            <select data-testid="time-entry-category" value={form.category} onChange={set("category")} className={sel}>
              {TIME_CATEGORIES.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
            </select>
          </div>
          <div>
            <Label>Note <span className="text-neutral-400 font-normal">(optionnel)</span></Label>
            <textarea data-testid="time-entry-note" value={form.note} onChange={set("note")} rows={2}
              placeholder="Ex : création d'un carrousel Instagram"
              className="mt-1.5 w-full text-sm border border-neutral-200 rounded-md px-2.5 py-2 bg-white" />
          </div>
          <Button data-testid="time-entry-submit" onClick={submit}
            disabled={loading || !form.client_id || !form.date || !form.start_time || !form.end_time}
            className="w-full bg-neutral-900 hover:bg-neutral-800 text-white">
            {loading ? "Enregistrement…" : entry ? "Enregistrer les modifications" : "Ajouter"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
